import React, { useEffect, useRef, useState } from 'react';
import './EditableLabel.scss';
import Button from './Button';
import { ReactComponent as CheckIcon } from '../assets/check.svg';
import { ReactComponent as CloseIcon } from '../assets/close_medium.svg';
import { ReactComponent as EditIcon } from '../assets/edit_large.svg';

interface EditableLabelProps {
  label: string;
  itemId: string;
  onSave: (newValue: string) => void;
}

const EditableLabel: React.FC<EditableLabelProps> = ({
  label,
  itemId,
  onSave
}) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(label);
  const inputRef = useRef<HTMLInputElement>(null);
  const prevItemIdRef = useRef<string>();

  const handleSave = () => {
    onSave(value);
    setEditing(false);
  };

  const handleCancel = () => {
    setValue(label);
    setEditing(false);
  };

  useEffect(() => {
    if (!editing) {
      setValue(label);
    }
  }, [label, editing]);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.setSelectionRange(
        inputRef.current.value.length,
        inputRef.current.value.length
      );
    }
  }, [editing]);

  useEffect(() => {
    if (prevItemIdRef.current && prevItemIdRef.current !== itemId) {
      setEditing(false);
      setValue(label);
    }
    prevItemIdRef.current = itemId;
  }, [itemId, label]);

  return (
    <div className="editable-label">
      {editing ? (
        <>
          <input
            ref={inputRef}
            name="label-value"
            type="text"
            className="editable-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                handleSave();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                handleCancel();
              }
            }}
          />
          <div className="button-container">
            <Button
              isIcon={true}
              onClick={handleSave}
              title="Save (Enter)"
              aria-label="Save"
            >
              <CheckIcon />
            </Button>
            <Button
              isIcon={true}
              onClick={handleCancel}
              title="Cancel (Esc)"
              aria-label="Cancel"
            >
              <CloseIcon />
            </Button>
          </div>
        </>
      ) : (
        <>
          <div className="button-container">
            <Button
              isIcon={true}
              onClick={() => setEditing(true)}
              title="Edit"
              aria-label="Edit"
            >
              <EditIcon />
            </Button>
          </div>
          <span className="label-text" onDoubleClick={() => setEditing(true)}>
            {value}
          </span>
        </>
      )}
    </div>
  );
};

export default EditableLabel;
